import { useState, useEffect, useCallback } from 'react'
import { ecrituresApi, contactsApi } from '../api'
import { useApp } from '../AppContext'
import { FormGroup, Empty, Loading } from '../components/ui'

const MODES = ['Chèque', 'Virement', 'Espèces', 'Carte bancaire', 'Prélèvement', 'Paiement en ligne', 'Autre']

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—'

const fmt = (n, devise = '') => {
  const num = parseFloat(n) || 0
  const [intPart, decPart] = num.toFixed(2).split('.')
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return `${intFormatted},${decPart}${devise ? ' ' + devise : ''}`
}

export default function Comptabilite() {
  const { toast, devise } = useApp()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)   // null | {} | ecriture
  const [catModal, setCatModal] = useState(false)
  const [tab, setTab] = useState('tous') // 'tous' | 'depenses' | 'recettes'
  const [filters, setFilters] = useState({
    du: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    au: new Date().toISOString().split('T')[0],
  })

  const load = useCallback(() => {
    setLoading(true)
    const p = { du: filters.du, au: filters.au }
    if (tab !== 'tous') p.type = tab === 'depenses' ? 'depense' : 'recette'
    ecrituresApi.list(p)
      .then(r => setData(r.data))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [filters, tab])

  useEffect(() => { load() }, [load])

  async function del(id) {
    if (!confirm('Supprimer cette écriture ?')) return
    try { await ecrituresApi.delete(id); toast('Supprimée', 'success'); load() }
    catch (e) { toast(e.message, 'error') }
  }

  const items = data?.items || []

  return (
    <div className="content">

      {/* Toolbar période */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: 'var(--text3)' }}>Période :</span>
        <input
          type="date"
          className="form-control"
          style={{ width: 160 }}
          value={filters.du}
          onChange={e => setFilters(f => ({ ...f, du: e.target.value }))}
        />
        <span style={{ fontSize: 13, color: 'var(--text3)' }}>au</span>
        <input
          type="date"
          className="form-control"
          style={{ width: 160 }}
          value={filters.au}
          onChange={e => setFilters(f => ({ ...f, au: e.target.value }))}
        />

        {/* Raccourcis période */}
        {[
          { label: 'Ce mois', du: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], au: new Date().toISOString().split('T')[0] },
          { label: 'Mois préc.', du: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().split('T')[0], au: new Date(new Date().getFullYear(), new Date().getMonth(), 0).toISOString().split('T')[0] },
          { label: 'Cette année', du: `${new Date().getFullYear()}-01-01`, au: new Date().toISOString().split('T')[0] },
        ].map(p => (
          <button key={p.label} className="btn sm" onClick={() => setFilters({ du: p.du, au: p.au })}>{p.label}</button>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => setCatModal(true)}>
            <i className="ti ti-tag" /> Catégories
          </button>
          <button className="btn primary" onClick={() => setModal({})}>
            <i className="ti ti-plus" /> Saisir une écriture
          </button>
        </div>
      </div>

      {/* KPIs */}
      {data && (
        <div className="grid3" style={{ marginBottom: 18 }}>
          <div style={kpiStyle('#fce4ec', '#c62828')}>
            <i className="ti ti-trending-down" style={{ fontSize: 24 }} />
            <div>
              <div style={{ fontSize: 11, marginBottom: 2 }}>Total dépenses</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(data.total_depenses, devise)}</div>
            </div>
          </div>
          <div style={kpiStyle('#e8f5e9', '#2e7d32')}>
            <i className="ti ti-trending-up" style={{ fontSize: 24 }} />
            <div>
              <div style={{ fontSize: 11, marginBottom: 2 }}>Total recettes</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(data.total_recettes, devise)}</div>
            </div>
          </div>
          <div style={kpiStyle(data.solde >= 0 ? '#e8f5e9' : '#fce4ec', data.solde >= 0 ? '#2e7d32' : '#c62828')}>
            <i className="ti ti-scale" style={{ fontSize: 24 }} />
            <div>
              <div style={{ fontSize: 11, marginBottom: 2 }}>Solde</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{data.solde >= 0 ? '+' : ''}{fmt(data.solde, devise)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { id: 'tous', label: `Tout (${items.length})` },
          { id: 'depenses', label: `Dépenses (${items.filter(i => i.type === 'depense').length})` },
          { id: 'recettes', label: `Recettes (${items.filter(i => i.type === 'recette').length})` },
          { id: 'bilan', label: 'Bilan' },
        ].map(t => (
          <div key={t.id} className={`tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </div>
        ))}
      </div>

      {/* Liste écritures */}
      {tab !== 'bilan' && (loading ? <Loading /> : items.length === 0 ? (
        <Empty icon="calculator" message="Aucune écriture sur cette période" />
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Référence</th>
                  <th>Client / Fournisseur</th>
                  <th>Intitulé</th>
                  <th>Catégorie</th>
                  <th style={{ textAlign: 'right' }}>Dépenses</th>
                  <th style={{ textAlign: 'right' }}>Recettes</th>
                  <th>Mode</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(e => (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDate(e.date_ecriture)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>{e.reference || '—'}</td>
                    <td style={{ fontSize: 13 }}>{e.contact_nom || '—'}</td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{e.intitule}</div>
                      {e.commentaires && (
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                          {e.commentaires.substring(0, 50)}{e.commentaires.length > 50 ? '…' : ''}
                        </div>
                      )}
                    </td>
                    <td>
                      {e.categorie_nom
                        ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: e.type === 'depense' ? '#fce4ec' : '#e8f5e9', color: e.type === 'depense' ? '#c62828' : '#2e7d32' }}>{e.categorie_nom}</span>
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--danger)', fontWeight: 600 }}>
                      {e.type === 'depense' ? fmt(e.montant, devise) : ''}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 600 }}>
                      {e.type === 'recette' ? fmt(e.montant, devise) : ''}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>{e.mode_reglement || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn sm" onClick={() => setModal(e)} title="Modifier">
                          <i className="ti ti-edit" />
                        </button>
                        <button className="btn sm" onClick={() => del(e.id)} title="Supprimer">
                          <i className="ti ti-trash" style={{ color: 'var(--danger)' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Totaux en pied de tableau */}
              <tfoot>
                <tr style={{ background: 'var(--bg2)' }}>
                  <td colSpan={5} style={{ padding: '10px 14px', fontWeight: 700, fontSize: 13 }}>Total</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>
                    {fmt(data?.total_depenses, devise)}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>
                    {fmt(data?.total_recettes, devise)}
                  </td>
                  <td colSpan={2} style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: data?.solde >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                    Solde : {data?.solde >= 0 ? '+' : ''}{fmt(data?.solde, devise)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ))}
      {tab === 'bilan' && data && (
        <div>
          {/* Résumé mensuel */}
          <div className="grid2" style={{ marginBottom: 18 }}>
            <div className="card">
              <div className="card-title">Résumé de la période</div>
              <table style={{ width: '100%', fontSize: 13 }}>
                <tbody>
                  {[
                    { label: 'Total recettes', val: data.total_recettes, color: 'var(--success)' },
                    { label: 'Total dépenses', val: data.total_depenses, color: 'var(--danger)' },
                    { label: 'Résultat net', val: data.solde, color: data.solde >= 0 ? 'var(--success)' : 'var(--danger)', bold: true },
                  ].map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 0', color: 'var(--text2)' }}>{r.label}</td>
                      <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: r.bold ? 700 : 500, color: r.color, fontSize: r.bold ? 16 : 13 }}>
                        {r.val >= 0 ? '' : '-'}{fmt(Math.abs(r.val), devise)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card">
              <div className="card-title">Répartition dépenses par catégorie</div>
              {(() => {
                const bycat = items.filter(i => i.type === 'depense').reduce((acc, i) => {
                  const k = i.categorie_nom || 'Non classé'
                  acc[k] = (acc[k] || 0) + parseFloat(i.montant)
                  return acc
                }, {})
                const total = Object.values(bycat).reduce((a, b) => a + b, 0)
                return Object.entries(bycat).sort((a, b) => b[1] - a[1]).map(([nom, val], i) => (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                      <span>{nom}</span>
                      <span style={{ fontWeight: 600 }}>{fmt(val, devise)} ({total > 0 ? Math.round(val / total * 100) : 0}%)</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${total > 0 ? val / total * 100 : 0}%`, height: '100%', background: 'var(--danger)', borderRadius: 3 }} />
                    </div>
                  </div>
                ))
              })()}
            </div>
          </div>

          {/* Répartition recettes par mode de règlement */}
          <div className="card">
            <div className="card-title">Recettes par mode de règlement</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {(() => {
                const bymode = items.filter(i => i.type === 'recette').reduce((acc, i) => {
                  const k = i.mode_reglement || 'Non précisé'
                  acc[k] = (acc[k] || 0) + parseFloat(i.montant)
                  return acc
                }, {})
                return Object.entries(bymode).sort((a, b) => b[1] - a[1]).map(([mode, val], i) => (
                  <div key={i} style={{ background: 'var(--primary-light)', border: '1px solid var(--g100)', borderRadius: 'var(--radius)', padding: '12px 16px', minWidth: 160 }}>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>{mode}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{fmt(val, devise)}</div>
                  </div>
                ))
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal saisie écriture */}
      {modal !== null && (
        <EcritureModal
          ecriture={modal}
          devise={devise}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); toast('Écriture enregistrée', 'success') }}
        />
      )}

      {/* Modal catégories */}
      {catModal && (
        <CategoriesModal
          onClose={() => setCatModal(false)}
          toast={toast}
        />
      )}
    </div>
  )
}

// ── Modal saisie / édition écriture ───────────────────────────
function EcritureModal({ ecriture, devise, onClose, onSaved }) {
  const { toast } = useApp()
  const [form, setForm] = useState({
    type: ecriture?.type || 'depense',
    date_ecriture: ecriture?.date_ecriture || new Date().toISOString().split('T')[0],
    intitule: ecriture?.intitule || '',
    reference: ecriture?.reference || '',
    montant: ecriture?.montant || '',
    montant_taxes: ecriture?.montant_taxes || '',
    mode_reglement: ecriture?.mode_reglement || 'Chèque',
    contact_id: ecriture?.contact_id || '',
    categorie_id: ecriture?.categorie_id || '',
    commentaires: ecriture?.commentaires || '',
  })
  const [contacts, setContacts] = useState([])
  const [categories, setCategories] = useState([])
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  useEffect(() => {
    contactsApi.list({ per_page: 200 }).then(r => setContacts(r.data || [])).catch(() => { })
    ecrituresApi.categories({ type: form.type }).then(r => setCategories(r.data || [])).catch(() => { })
  }, [])

  // Recharger les catégories quand le type change
  useEffect(() => {
    ecrituresApi.categories({ type: form.type }).then(r => setCategories(r.data || [])).catch(() => { })
    setForm(p => ({ ...p, categorie_id: '' }))
  }, [form.type])

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try {
      if (ecriture?.id) await ecrituresApi.update(ecriture.id, form)
      else await ecrituresApi.create(form)
      onSaved()
    } catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">{ecriture?.id ? 'Modifier l\'écriture' : 'Saisir une écriture'}</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">

            {/* Type — dépense ou recette */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {[
                { v: 'depense', label: '↓ Dépense', color: '#c62828', bg: '#fce4ec' },
                { v: 'recette', label: '↑ Recette', color: '#2e7d32', bg: '#e8f5e9' },
              ].map(t => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, type: t.v }))}
                  style={{
                    flex: 1, padding: '10px', border: `2px solid ${form.type === t.v ? t.color : 'var(--border)'}`,
                    borderRadius: 'var(--radius)', background: form.type === t.v ? t.bg : 'var(--bg)',
                    color: form.type === t.v ? t.color : 'var(--text3)',
                    fontWeight: form.type === t.v ? 700 : 400, fontSize: 14, cursor: 'pointer', transition: 'all .15s'
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="grid2">
              <FormGroup label="Date" required>
                <input type="date" className="form-control" value={form.date_ecriture} onChange={f('date_ecriture')} required />
              </FormGroup>
              <FormGroup label="Type de catégorie">
                <select className="form-control" value={form.categorie_id} onChange={f('categorie_id')}>
                  <option value="">— Sélectionner —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </FormGroup>
            </div>

            <FormGroup label="Intitulé" required>
              <input className="form-control" value={form.intitule} onChange={f('intitule')} placeholder="Description de l'écriture…" required />
            </FormGroup>

            <FormGroup label="Référence">
              <input className="form-control" value={form.reference} onChange={f('reference')} placeholder="N° facture, chèque…" />
            </FormGroup>

            <div className="grid2">
              <FormGroup label={`Montant (${devise})`} required>
                <input
                  type="number"
                  className="form-control"
                  value={form.montant}
                  onChange={f('montant')}
                  min={0}
                  step="any"
                  placeholder="0"
                  required
                />
              </FormGroup>
              <FormGroup label={`dont taxes (${devise})`}>
                <input
                  type="number"
                  className="form-control"
                  value={form.montant_taxes}
                  onChange={f('montant_taxes')}
                  min={0}
                  step="any"
                  placeholder="0"
                />
              </FormGroup>
            </div>

            <FormGroup label="Mode de règlement">
              <select className="form-control" value={form.mode_reglement} onChange={f('mode_reglement')}>
                {MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </FormGroup>

            <FormGroup label="Client / Fournisseur">
              <select className="form-control" value={form.contact_id} onChange={f('contact_id')}>
                <option value="">— Aucun —</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </FormGroup>

            <FormGroup label="Commentaires">
              <textarea className="form-control" rows={3} value={form.commentaires} onChange={f('commentaires')} placeholder="Notes, détails…" />
            </FormGroup>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>
              {saving ? '…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Modal gestion catégories ───────────────────────────────────
function CategoriesModal({ onClose, toast }) {
  const [cats, setCats] = useState([])
  const [loading, setLoading] = useState(true)
  const [newNom, setNewNom] = useState('')
  const [newType, setNewType] = useState('depense')
  const [saving, setSaving] = useState(false)

  const load = () => {
    ecrituresApi.categories()
      .then(r => setCats(r.data || []))
      .catch(() => { })
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  async function add() {
    if (!newNom.trim()) return
    setSaving(true)
    try {
      await ecrituresApi.createCategorie({ nom: newNom.trim(), type: newType })
      setNewNom('')
      load()
      toast('Catégorie ajoutée', 'success')
    } catch (e) { toast(e.message, 'error') }
    finally { setSaving(false) }
  }

  async function del(id) {
    if (!confirm('Supprimer cette catégorie ?')) return
    try {
      await ecrituresApi.deleteCategorie(id)
      setCats(cats.filter(c => c.id !== id))
      toast('Supprimée', 'success')
    } catch (e) { toast(e.message, 'error') }
  }

  const depenses = cats.filter(c => c.type === 'depense')
  const recettes = cats.filter(c => c.type === 'recette')

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <span className="modal-title">Gérer les catégories</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div className="modal-body">
          {loading ? <Loading /> : (
            <>
              {/* Dépenses */}
              <p style={{ fontSize: 11, fontWeight: 700, color: '#c62828', letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 8 }}>
                Dépenses
              </p>
              {depenses.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13 }}><i className="ti ti-tag" style={{ marginRight: 6, color: '#c62828' }} />{c.nom}</span>
                  <button className="btn sm" onClick={() => del(c.id)}><i className="ti ti-trash" style={{ color: 'var(--danger)' }} /></button>
                </div>
              ))}
              {depenses.length === 0 && <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>Aucune catégorie dépense</p>}

              {/* Recettes */}
              <p style={{ fontSize: 11, fontWeight: 700, color: '#2e7d32', letterSpacing: '.05em', textTransform: 'uppercase', margin: '16px 0 8px' }}>
                Recettes
              </p>
              {recettes.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13 }}><i className="ti ti-tag" style={{ marginRight: 6, color: '#2e7d32' }} />{c.nom}</span>
                  <button className="btn sm" onClick={() => del(c.id)}><i className="ti ti-trash" style={{ color: 'var(--danger)' }} /></button>
                </div>
              ))}
              {recettes.length === 0 && <p style={{ fontSize: 13, color: 'var(--text3)' }}>Aucune catégorie recette</p>}
            </>
          )}

          {/* Ajouter */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <select className="form-control" style={{ width: 130 }} value={newType} onChange={e => setNewType(e.target.value)}>
              <option value="depense">Dépense</option>
              <option value="recette">Recette</option>
            </select>
            <input
              className="form-control"
              placeholder="Nouvelle catégorie…"
              value={newNom}
              onChange={e => setNewNom(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && add()}
              style={{ flex: 1 }}
            />
            <button className="btn primary" onClick={add} disabled={saving || !newNom.trim()}>
              <i className="ti ti-plus" />
            </button>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn primary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  )
}

// ── Style helper ──────────────────────────────────────────────
const kpiStyle = (bg, color) => ({
  background: bg, color,
  border: `1px solid ${color}33`,
  borderRadius: 'var(--radius-lg)',
  padding: '16px 20px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
})